import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Button,
  VStack,
  HStack,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Alert,
  AlertIcon,
  Spinner,
  Badge,
  useToast,
  Divider,
  Image,
} from '@chakra-ui/react';
import { FiSearch, FiUpload, FiCheck, FiX, FiEye } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

/**
 * OCR検証ページ
 * 既存のOCR結果と新しい画像をアップロードして比較検証する機能
 */
const OCRVerification = () => {
  const { currentUser } = useAuth();
  const toast = useToast();

  // 状態管理
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [ocrResults, setOcrResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [verificationResults, setVerificationResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // コンポーネント読み込み時にプロジェクト一覧を取得
  useEffect(() => {
    fetchProjects();
  }, []);

  // 選択されたプロジェクトのOCR結果を取得
  useEffect(() => {
    if (selectedProject) {
      fetchOcrResults(selectedProject);
    }
  }, [selectedProject]);

  /**
   * プロジェクト一覧を取得
   */
  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('プロジェクト取得エラー:', error);
      toast({
        title: 'エラー',
        description: 'プロジェクト一覧の取得に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /**
   * 指定プロジェクトのOCR結果一覧を取得
   */
  const fetchOcrResults = async (projectId) => {
    try {
      const response = await fetch(`/api/projects/${projectId}/ocr-results`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOcrResults(data);
      }
    } catch (error) {
      console.error('OCR結果取得エラー:', error);
      toast({
        title: 'エラー',
        description: 'OCR結果の取得に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /**
   * 画像ファイルのアップロード処理
   */
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadedImage({
            file: file,
            preview: e.target.result,
            name: file.name
          });
        };
        reader.readAsDataURL(file);
      } else {
        toast({
          title: 'エラー',
          description: '画像ファイルを選択してください',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  /**
   * OCR結果の検証実行
   */
  const executeVerification = async () => {
    if (!selectedResult || !uploadedImage) {
      toast({
        title: '選択不完了',
        description: 'OCR結果と検証画像の両方を選択してください',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', uploadedImage.file);
      formData.append('original_result_id', selectedResult.id);

      const response = await fetch('/api/ocr/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setVerificationResults(data);
        toast({
          title: '検証完了',
          description: 'OCR検証が完了しました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('検証処理に失敗しました');
      }
    } catch (error) {
      console.error('OCR検証エラー:', error);
      toast({
        title: 'エラー',
        description: 'OCR検証処理に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 検索フィルター処理
  const filteredResults = ocrResults.filter(result =>
    result.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
    result.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* ページヘッダー */}
        <Box>
          <Heading size="lg" mb={2}>OCR結果検証</Heading>
          <Text color="gray.600">
            既存のOCR結果と新しい画像をアップロードして、データの正確性を検証できます
          </Text>
        </Box>

        {/* プロジェクト選択セクション */}
        <Card>
          <CardHeader>
            <Heading size="md">1. プロジェクト選択</Heading>
          </CardHeader>
          <CardBody>
            <Select
              placeholder="検証するプロジェクトを選択"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Select>
          </CardBody>
        </Card>

        {/* OCR結果選択セクション */}
        {selectedProject && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">2. OCR結果選択</Heading>
                <InputGroup maxW="300px">
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder="結果を検索..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </HStack>
            </CardHeader>
            <CardBody>
              {filteredResults.length > 0 ? (
                <Grid templateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={4}>
                  {filteredResults.map(result => (
                    <Card
                      key={result.id}
                      cursor="pointer"
                      border={selectedResult?.id === result.id ? "2px solid" : "1px solid"}
                      borderColor={selectedResult?.id === result.id ? "blue.500" : "gray.200"}
                      _hover={{ borderColor: "blue.300" }}
                      onClick={() => setSelectedResult(result)}
                    >
                      <CardBody>
                        <VStack align="start" spacing={2}>
                          <Text fontWeight="bold" noOfLines={1}>
                            {result.filename}
                          </Text>
                          <Text fontSize="sm" color="gray.600" noOfLines={2}>
                            {result.title || '無題'}
                          </Text>
                          <HStack>
                            <Badge colorScheme="blue">
                              {result.ai_provider}
                            </Badge>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(result.created_at).toLocaleDateString('ja-JP')}
                            </Text>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  ))}
                </Grid>
              ) : (
                <Text color="gray.500">OCR結果が見つかりません</Text>
              )}
            </CardBody>
          </Card>
        )}

        {/* 画像アップロードセクション */}
        {selectedResult && (
          <Card>
            <CardHeader>
              <Heading size="md">3. 検証画像アップロード</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  display="none"
                  id="image-upload"
                />
                <Button
                  as="label"
                  htmlFor="image-upload"
                  leftIcon={<FiUpload />}
                  colorScheme="blue"
                  variant="outline"
                  cursor="pointer"
                >
                  画像をアップロード
                </Button>

                {uploadedImage && (
                  <Box textAlign="center">
                    <Image
                      src={uploadedImage.preview}
                      alt="アップロード画像"
                      maxH="200px"
                      mb={2}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                    />
                    <Text fontSize="sm" color="gray.600">
                      {uploadedImage.name}
                    </Text>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* 検証実行ボタン */}
        {selectedResult && uploadedImage && (
          <Box textAlign="center">
            <Button
              leftIcon={<FiCheck />}
              colorScheme="green"
              size="lg"
              onClick={executeVerification}
              isLoading={isLoading}
              loadingText="検証中..."
            >
              OCR検証を実行
            </Button>
          </Box>
        )}

        {/* 検証結果表示セクション */}
        {verificationResults && (
          <Card>
            <CardHeader>
              <Heading size="md">検証結果</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {/* 総合結果 */}
                <Alert status={verificationResults.overall_match ? "success" : "warning"}>
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">
                      総合的な一致度: {verificationResults.match_percentage}%
                    </Text>
                    <Text fontSize="sm">
                      {verificationResults.overall_match ?
                        '既存の結果と高い一致率を示しています' :
                        '既存の結果と相違があります。確認が必要です'
                      }
                    </Text>
                  </Box>
                </Alert>

                <Divider />

                {/* 詳細比較結果 */}
                <Box>
                  <Text fontWeight="bold" mb={3}>詳細比較</Text>
                  <Grid templateColumns="1fr 1fr" gap={6}>
                    <GridItem>
                      <Text fontWeight="semibold" mb={2}>既存結果</Text>
                      <Box bg="gray.50" p={3} borderRadius="md" fontSize="sm">
                        {verificationResults.original_data}
                      </Box>
                    </GridItem>
                    <GridItem>
                      <Text fontWeight="semibold" mb={2}>新しい結果</Text>
                      <Box bg="gray.50" p={3} borderRadius="md" fontSize="sm">
                        {verificationResults.new_data}
                      </Box>
                    </GridItem>
                  </Grid>
                </Box>

                {/* 差異検出結果 */}
                {verificationResults.differences && verificationResults.differences.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={3}>検出された差異</Text>
                    <VStack spacing={2} align="stretch">
                      {verificationResults.differences.map((diff, index) => (
                        <Box key={index} p={3} bg="yellow.50" borderRadius="md" border="1px" borderColor="yellow.200">
                          <Text fontSize="sm">
                            <Text as="span" fontWeight="semibold">項目:</Text> {diff.field} <br />
                            <Text as="span" fontWeight="semibold">既存:</Text> {diff.original} <br />
                            <Text as="span" fontWeight="semibold">新規:</Text> {diff.new}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

export default OCRVerification;